import { useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '../../lib/supabase';
import {
  listHuddleParticipants,
  listHuddleTranscript,
  retryHuddleProcessing,
  signPlayback,
} from './huddleCardApi';
import type { HuddleParticipant, HuddleRow, HuddleTranscriptLine } from './huddleCardApi';

export interface HuddleMessageProps {
  client: SupabaseClient;
  huddle: HuddleRow;
  onJoin?: (huddle: HuddleRow) => void;
  canRetry?: boolean;
}
export function formatTranscriptTime(startMs: number): string {
  const seconds = Math.max(0, Math.floor(startMs / 1000));
  const two = (n: number) => String(n).padStart(2, '0');
  return seconds >= 3600
    ? `${two(Math.floor(seconds / 3600))}:${two(Math.floor(seconds / 60) % 60)}:${two(seconds % 60)}`
    : `${two(Math.floor(seconds / 60))}:${two(seconds % 60)}`;
}
export function formatDuration(startedAt: string, endedAt: string | null): string {
  return formatTranscriptTime(
    Math.max(0, (endedAt === null ? Date.now() : Date.parse(endedAt)) - Date.parse(startedAt)),
  );
}
const date = (value: string) =>
  new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value));
export function HuddleMessage(props: HuddleMessageProps) {
  // Dropping an old card also drops its private signed URL and pending reads.
  return (
    <HuddleMessageInner
      key={`${props.huddle.id}:${props.huddle.playback_path}:${props.huddle.playback_deleted_at}`}
      {...props}
    />
  );
}
function HuddleMessageInner({ client, huddle, onJoin, canRetry = false }: HuddleMessageProps) {
  const [participants, setParticipants] = useState<HuddleParticipant[]>([]);
  const [participantError, setParticipantError] = useState(false);
  const [transcript, setTranscript] = useState<HuddleTranscriptLine[] | null>(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(false);
  const [pendingRetry, setPendingRetry] = useState<'playback' | 'transcript' | null>(null);
  const [retryError, setRetryError] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [playbackUnavailable, setPlaybackUnavailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const alive = useRef(true);
  const signing = useRef(false);
  const playbackErrors = useRef(0);
  const pendingSeek = useRef<number | null>(null);
  const revision = `${huddle.state}:${huddle.playback_state}:${huddle.transcript_state}:${huddle.transcribed_at}`;
  const currentRevision = useRef(revision);
  currentRevision.current = revision;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    let current = true;
    void listHuddleParticipants(client, huddle.id).then(
      (rows) => {
        if (current) setParticipants(rows);
      },
      () => {
        if (current) setParticipantError(true);
      },
    );
    return () => {
      current = false;
    };
  }, [client, huddle.id]);
  useEffect(() => {
    setPendingRetry(null);
    setRetryError(false);
    setTranscript(null);
    setTranscriptError(false);
  }, [revision]);
  useEffect(() => {
    if (pendingRetry === null) return;
    const timer = setTimeout(() => {
      setPendingRetry(null);
    }, 30000);
    return () => clearTimeout(timer);
  }, [pendingRetry]);
  const playbackReady =
    huddle.playback_state === 'ready' &&
    huddle.playback_path !== null &&
    huddle.playback_deleted_at === null;
  async function loadTranscript() {
    if (transcript !== null || transcriptLoading) return;
    const readRevision = revision;
    setTranscriptLoading(true);
    setTranscriptError(false);
    try {
      const rows = await listHuddleTranscript(client, huddle.id);
      if (alive.current && currentRevision.current === readRevision) setTranscript(rows);
    } catch {
      if (alive.current && currentRevision.current === readRevision) setTranscriptError(true);
    } finally {
      if (alive.current) setTranscriptLoading(false);
    }
  }
  async function requestPlayback(renew = false) {
    if (
      !playbackReady ||
      signing.current ||
      playbackUnavailable ||
      (!renew && playbackUrl !== null)
    )
      return;
    if (renew && playbackErrors.current++ >= 1) {
      setPlaybackUnavailable(true);
      return;
    }
    signing.current = true;
    if (renew) pendingSeek.current = audioRef.current?.currentTime ?? 0;
    try {
      const url = await signPlayback(
        client,
        huddle.playback_path!,
        huddle.playback_duration_ms ?? 0,
      );
      if (alive.current) setPlaybackUrl(url);
    } catch {
      if (alive.current) setPlaybackUnavailable(true);
    } finally {
      signing.current = false;
    }
  }
  async function retry() {
    if (retryBusy || !canRetry) return;
    const requestedRevision = revision;
    const stage = huddle.playback_state === 'failed' ? 'playback' : 'transcript';
    setRetryBusy(true);
    setRetryError(false);
    try {
      await retryHuddleProcessing(client, huddle.id);
      if (alive.current && currentRevision.current === requestedRevision) setPendingRetry(stage);
    } catch {
      if (alive.current) setRetryError(true);
    } finally {
      if (alive.current) setRetryBusy(false);
    }
  }
  const transcriptState = pendingRetry === 'transcript' ? 'pending' : huddle.transcript_state;
  const playbackState = pendingRetry === 'playback' ? 'pending' : huddle.playback_state;
  const retryable =
    pendingRetry === null && (playbackState === 'failed' || transcriptState === 'failed');
  return (
    <section className="huddle-message" aria-label="Huddle">
      <p className="huddle-message-eyebrow">
        Huddle · {date(huddle.started_at)}
        {huddle.state === 'live'
          ? null
          : ` · ${formatDuration(huddle.started_at, huddle.ended_at)}`}
      </p>
      <h3 className="huddle-message-title">
        {huddle.state === 'live'
          ? 'Huddle in progress'
          : playbackReady && !playbackUnavailable
            ? 'Huddle recording'
            : 'Huddle ended'}
      </h3>
      <p className="huddle-message-people">
        {participantError
          ? 'Participant list unavailable'
          : participants.length === 0
            ? 'No seats recorded'
            : [...new Set(participants.map((p) => p.name))].join(', ')}
      </p>
      {huddle.state === 'live' ? (
        <p>
          {participants.filter((p) => p.left_at === null).length} of {huddle.max_participants} seats
          occupied{' '}
          {onJoin ? (
            <button className="huddle-message-action" type="button" onClick={() => onJoin(huddle)}>
              Join
            </button>
          ) : null}
        </p>
      ) : (
        <>
          {huddle.playback_deleted_at !== null ? (
            <p>Recording removed on {date(huddle.playback_deleted_at)}</p>
          ) : playbackReady ? (
            playbackUnavailable ? (
              <p>Playback not available</p>
            ) : (
              <>
                {playbackUrl === null ? (
                  <button
                    className="huddle-message-action huddle-message-action--primary"
                    type="button"
                    onClick={() => void requestPlayback()}
                  >
                    Load recording
                  </button>
                ) : null}
                <audio
                  ref={audioRef}
                  controls
                  hidden={playbackUrl === null}
                  preload="none"
                  src={playbackUrl ?? undefined}
                  onClick={() => void requestPlayback()}
                  onPlay={() => void requestPlayback()}
                  onError={() => void requestPlayback(true)}
                  onPlaying={() => {
                    playbackErrors.current = 0;
                  }}
                  onLoadedMetadata={() => {
                    if (audioRef.current && pendingSeek.current !== null) {
                      audioRef.current.currentTime = pendingSeek.current;
                      pendingSeek.current = null;
                    }
                  }}
                />
              </>
            )
          ) : playbackState === 'empty' ? (
            <p>No audio was recorded</p>
          ) : playbackState === 'failed' || huddle.playback_state === 'ready' ? (
            <p>Recording unavailable. Some microphone audio may not have uploaded.</p>
          ) : (
            <p>Recording pending. Processing waits while the office Mac is asleep.</p>
          )}
          {playbackReady && !playbackUnavailable ? (
            <p className="huddle-recording-note">
              Saved audio may have gaps after a microphone or upload interruption.
            </p>
          ) : null}
          {transcriptState === 'pending' ? (
            <p>Transcript pending</p>
          ) : transcriptState === 'failed' ? (
            <p>Transcript failed. You can still use any available recording.</p>
          ) : transcriptState === 'too_long' ? (
            <p>Transcript unavailable: this call exceeds the transcription limit.</p>
          ) : transcriptState === 'empty' ? (
            <p>No speech was transcribed.</p>
          ) : (
            <>
              <button
                className="huddle-transcript-toggle"
                type="button"
                aria-expanded={transcriptOpen}
                onClick={() => {
                  setTranscriptOpen(!transcriptOpen);
                  if (!transcriptOpen) void loadTranscript();
                }}
              >
                {transcript === null ? 'Transcript' : `Transcript (${transcript.length} lines)`}
              </button>
              {transcriptOpen ? (
                transcriptLoading ? (
                  <p>Loading transcript...</p>
                ) : transcriptError ? (
                  <>
                    <p role="alert">Transcript could not be loaded.</p>
                    <button
                      className="huddle-message-action"
                      type="button"
                      onClick={() => void loadTranscript()}
                    >
                      Load transcript again
                    </button>
                  </>
                ) : transcript?.length === 0 ? (
                  <p>No transcript lines were returned.</p>
                ) : (
                  transcript?.map((line) => (
                    <p className="huddle-transcript-line" key={line.id}>
                      <button
                        className="huddle-transcript-time"
                        type="button"
                        disabled={!playbackReady || playbackUrl === null}
                        onClick={() => {
                          if (audioRef.current) audioRef.current.currentTime = line.start_ms / 1000;
                        }}
                      >
                        [{formatTranscriptTime(line.start_ms)}]
                      </button>{' '}
                      {line.speaker_name}: {line.text}
                    </p>
                  ))
                )
              ) : null}
            </>
          )}
          {retryable && canRetry ? (
            <button
              className="huddle-message-action"
              type="button"
              disabled={retryBusy}
              onClick={() => void retry()}
            >
              {retryBusy ? 'Starting retry...' : 'Try again'}
            </button>
          ) : null}
          {retryError ? <p role="alert">Retry could not be started.</p> : null}
        </>
      )}
    </section>
  );
}
